import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load non-critical pages for performance
const Calculators = lazy(() => import("./pages/Calculators"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Quiz = lazy(() => import("./pages/Quiz"));
const PreApproval = lazy(() => import("./pages/PreApproval"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Residential = lazy(() => import("./pages/services/Residential"));
const InvestmentProperties = lazy(() => import("./pages/services/InvestmentProperties"));
const Commercial = lazy(() => import("./pages/services/Commercial"));
const ALending = lazy(() => import("./pages/services/ALending"));
const BLending = lazy(() => import("./pages/services/BLending"));
const PrivateLending = lazy(() => import("./pages/services/PrivateLending"));
const DSCRLoans = lazy(() => import("./pages/services/DSCRLoans"));
const BridgeLoans = lazy(() => import("./pages/services/BridgeLoans"));
const ConstructionFinancing = lazy(() => import("./pages/services/ConstructionFinancing"));
const ScenarioAssistant = lazy(() => import("./pages/ScenarioAssistant"));
const CapitalPartners = lazy(() => import("./pages/CapitalPartners"));
const ComplianceScreeningTest = lazy(() => import("./pages/ComplianceScreeningTest"));
const BorrowCommercial = lazy(() => import("./pages/BorrowCommercial"));
const InvestPrivate = lazy(() => import("./pages/InvestPrivate"));
const WhyAtralend = lazy(() => import("./pages/WhyAtralend"));
// Auth pages
const Auth = lazy(() => import("./pages/Auth"));

// Borrower portal pages
const BorrowerDashboard = lazy(() => import("./pages/borrower/Dashboard"));
const LoanApplication = lazy(() => import("./pages/borrower/LoanApplication"));
const DocumentUpload = lazy(() => import("./pages/borrower/DocumentUpload"));

// Investor portal pages
const InvestorDashboard = lazy(() => import("./pages/investor/Dashboard"));

// Admin pages
const LeadDashboard = lazy(() => import("./pages/admin/LeadDashboard"));
const CampaignAnalytics = lazy(() => import("./pages/admin/CampaignAnalytics"));
const GoogleDashboard = lazy(() => import("./pages/admin/GoogleDashboard"));
const AgentDashboard = lazy(() => import("./pages/admin/AgentDashboard"));
const ReferralAdmin = lazy(() => import("./pages/admin/ReferralAdmin"));
const SocialMediaQueue = lazy(() => import("./pages/admin/SocialMediaQueue"));
const InvestorAccreditation = lazy(() => import("./pages/investor/Accreditation"));

// Referral program
const ReferralDashboard = lazy(() => import("./pages/referral/ReferralDashboard"));

// Unsubscribe page
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/quiz" element={<Quiz />} />
              <Route path="/calculators" element={<Calculators />} />
              <Route path="/scenario-assistant" element={<ScenarioAssistant />} />
              <Route path="/compliance-test" element={<ComplianceScreeningTest />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/pre-approval" element={<PreApproval />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
               <Route path="/services/residential" element={<Residential />} />
               <Route path="/services/investment-properties" element={<InvestmentProperties />} />
               <Route path="/services/commercial" element={<Commercial />} />
               <Route path="/services/a-lending" element={<ALending />} />
               <Route path="/services/b-lending" element={<BLending />} />
               <Route path="/services/private-lending" element={<PrivateLending />} />
               <Route path="/services/dscr-loans" element={<DSCRLoans />} />
               <Route path="/services/bridge-loans" element={<BridgeLoans />} />
               <Route path="/services/construction-financing" element={<ConstructionFinancing />} />
               <Route path="/capital-partners" element={<CapitalPartners />} />
               <Route path="/why-atralend" element={<WhyAtralend />} />
               <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/borrow-commercial" element={<BorrowCommercial />} />
              <Route path="/invest-private" element={<InvestPrivate />} />
              
              {/* Protected borrower routes */}
              <Route
                path="/borrower/dashboard"
                element={
                  <ProtectedRoute>
                    <BorrowerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/borrower/apply"
                element={
                  <ProtectedRoute>
                    <LoanApplication />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/borrower/upload"
                element={
                  <ProtectedRoute>
                    <DocumentUpload />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected investor routes */}
              <Route
                path="/investor/dashboard"
                element={
                  <ProtectedRoute>
                    <InvestorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investor/accreditation"
                element={
                  <ProtectedRoute>
                    <InvestorAccreditation />
                  </ProtectedRoute>
                }
              />
              
              {/* Admin routes */}
              <Route
                path="/admin/leads"
                element={
                  <ProtectedRoute>
                    <LeadDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/campaigns"
                element={
                  <ProtectedRoute>
                    <CampaignAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/google"
                element={
                  <ProtectedRoute>
                    <GoogleDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/agents"
                element={
                  <ProtectedRoute>
                    <AgentDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/referrals"
                element={
                  <ProtectedRoute>
                    <ReferralAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/social"
                element={
                  <ProtectedRoute>
                    <SocialMediaQueue />
                  </ProtectedRoute>
                }
              />
              
              {/* Referral program */}
              <Route
                path="/referral"
                element={
                  <ProtectedRoute>
                    <ReferralDashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
